import React, {useCallback, useMemo, useState} from 'react';
import {Controller,useWatch} from 'react-hook-form';
import {FormError, FormLabel} from '../sharedStyles.tsx';
import {
    AlertCircle,
    Loader,
    RefreshCw,
    Upload,
    X,
    Check,
    MessageSquarePlus, ImageIcon
} from 'lucide-react';
import {
    ExpandedImageUploadComponent,
    FormComponentProps
} from '../../DynamicFormTypes.ts';
import {FormImageType, ImageSubmission} from "../../../../api/ApiTypes.ts";
import {useFormMethods, useFormStatic} from "../../DyanmicFormContext.tsx";
import {useFileApi} from "../../../../context/ApiContext.tsx";
import {ImageDisplay} from "./ImageDisplay.tsx";
import {defaultParserForSelects, getImageTypeDisplay} from "../../dyanimcFormUtil.tsx";
import {useValidationRules} from "../../hooks/useValidationRules.tsx";
import {FullSizeImageModal} from "./FullSizeImageModal.tsx";
import {debounce} from "lodash";

interface UploadingFile {
    id: string;
    file: File;
    error?: string;
}

const ExpandedImageUploadRenderer: React.FC<FormComponentProps<ExpandedImageUploadComponent>> = ({component}) => {

    const {formMethods} = useFormMethods()
    const {metaData, formPermissions} = useFormStatic();
    const fileApi = useFileApi();
    const {control} = formMethods;

    // Single Source Of Truth For the IMAGE VALUE (dont store state for it here)
    const uploadedImages = useWatch({
        control: formMethods.control,
        name: component.fieldKey,
        defaultValue: formMethods.getValues(component.fieldKey) || []
    }) as ImageSubmission[]

    //collect all the field keys that could be used for calculating requirements
    const allFieldKeys = useMemo<string[]>(() => {
        return component.requiredTypes?.filter(r => r.watchFieldKey)
            .map(f => f.watchFieldKey as string) ?? [];
    }, [component.requiredTypes])
    //watch the field keys that could be used for caluclating requirements
    const watchedDependencyValues = useWatch({ control: control, name: allFieldKeys, disabled: allFieldKeys.length === 0 });

    const requiredTypes = useMemo(() => {
        if (!component.requiredTypes) {
            return [];
        }

        return component.requiredTypes.flatMap(requirement => {
            let multiplier = requirement.hardCodedMultiplier ? requirement.hardCodedMultiplier : 1;
            if (requirement.watchFieldKey) {
                //extract the value
                const index = allFieldKeys.indexOf(requirement.watchFieldKey);
                const rawValue = watchedDependencyValues[index];
                let fieldValue = rawValue && typeof defaultParserForSelects(rawValue) === "object"
                    ? defaultParserForSelects(rawValue).value
                    : defaultParserForSelects(rawValue);

                if (requirement.evaluator && fieldValue !== requirement.evaluator) {
                    //if there is an evaluator && fieldValue not equal ignore it
                    return [];
                }
                if (!requirement.evaluator) {
                    if(fieldValue == null) {
                        fieldValue = 0;
                    }
                    multiplier = multiplier * fieldValue
                }
            }
            //create an empty list the size of the multiplier and fill it with the type
            return Array(multiplier).fill(requirement.type);
        })
    }, [allFieldKeys, watchedDependencyValues]);

    const { fulfilledTypes, unfulfilledTypes } = useMemo(() => {
        const fulfilled: Array<FormImageType> = [];
        //copy the list so we can play with it
        const remainingRequired = [...requiredTypes];
        //check what we have
        for (const image of uploadedImages) {
            // Find the index of the first matching requirement
            const matchIndex = remainingRequired.lastIndexOf(image.imageType);
            if (matchIndex !== -1) {
                fulfilled.push(image.imageType);
                // Remove from requirements so we don't double count
                remainingRequired.splice(matchIndex, 1);
            }
        }
        return { fulfilledTypes: fulfilled, unfulfilledTypes: remainingRequired };

    }, [uploadedImages, requiredTypes]);

    // Local state only for UI concerns that don't need to persist
    const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
    const [componentError, setComponentError] = useState<string | null>(null);
    const [updatingImageId, setUpdatingImageId] = useState<number | null>(null);
    const [selectedModalImage, setSelectedModalImage] = useState<ImageSubmission | null>(null);
    //use this for ui
    const sortedImages = useMemo(() => {
        return [...uploadedImages].sort((a, b) => {
            const valA = a.generalFileId;
            const valB = b.generalFileId;

            if (valA == null && valB == null) return 0;
            if (valA == null) return 1;  // nulls go to end
            if (valB == null) return -1;

            return valA - valB; // ascending (or valB - valA for descending)
        });
    }, [uploadedImages]);


    // Helper function to update the form value
    const setUploadedImages = useCallback((updater: ((prev: ImageSubmission[]) => ImageSubmission[])) => {
        if (!formPermissions.canEdit) {
            console.log("Edits or auto set values not allowed, image update aborted for", component.fieldKey);
            return;
        }

        const newValue = updater(uploadedImages);

        formMethods.setValue(component.fieldKey, newValue, {shouldValidate: true});

    }, [component.fieldKey, component.requiredTypes, formMethods, uploadedImages]);


    const uploadFile = useCallback(async (fileToUpload: File, tempId: string) => {
        if (!formPermissions.canEdit) {
            setComponentError("You do not have permission to upload images.");
            return;
        }

        try {
            const uploadedImage = await fileApi.uploadFormImage({
                file: fileToUpload,
                formMeta: metaData,
                imageType: FormImageType.NONE, // Always upload with default type NONE
                fieldKey: component.fieldKey,
                externalImageUrl: undefined
            });

            return uploadedImage;
        } catch (error) {
            console.error(`Failed to upload ${fileToUpload.name}:`, error);
            setUploadingFiles(prev => prev.map(f => f.id === tempId ? {...f, error: 'Upload failed'} : f));
        } finally {
            setUploadingFiles(prev => prev.filter(f => f.id !== tempId || f.error));
        }
    }, [metaData, component.fieldKey, fileApi, setUploadedImages, formPermissions.canEdit, uploadedImages]);


    const handleFileSelection = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files) return;
        const files = Array.from(event.target.files);
        if (event.target) event.target.value = '';

        // Add ALL files to uploading list upfront
        const filesToUpload = files
            .filter(file => {
                if (!file.type.startsWith('image/')) {
                    setComponentError(`${file.name} is not a valid image file.`);
                    return false;
                }
                return true;
            })
            .map(file => ({
                id: `uploading-${Date.now()}-${file.name}`,
                file
            }));

        setUploadingFiles(prev => [...prev, ...filesToUpload]);

        const uploadPromises = filesToUpload.map(({id, file}) => uploadFile(file, id));
        const results = await Promise.all(uploadPromises);

        const successfulImages = results.filter((img): img is ImageSubmission => !!img);
        if (successfulImages.length > 0) {
            setUploadedImages(prev => [...prev, ...successfulImages]);
        }
    }, [uploadFile]);

    const handleRemoveUploadedImage = useCallback(async (imageId: number) => {
        try {
            if (!formPermissions.canEdit) {
                setComponentError("You do not have permission to remove images.");
                return;
            }
            setUploadedImages(prev => {
                return prev.filter(img => img.generalFileId !== imageId);
            });
        } catch (error) {
            console.error('Image removal failed:', error);
            setComponentError('Failed to remove image');
        }
    }, [fileApi, setUploadedImages, formPermissions.canEdit]);

    const handleImageUpdate = useCallback(async (editedImage: ImageSubmission,
                                                 newType: FormImageType | undefined,
                                                 newShowsRepair: boolean | undefined,
                                                 newComments: string | undefined) => {
        if (!formPermissions.canEdit) {
            setComponentError("You do not have permission to update image types.");
            return;
        }
        setUpdatingImageId(editedImage.generalFileId);
        setComponentError(null);
        const updatedImage = {...editedImage};
        if (newType !== undefined) {
            updatedImage.imageType = newType;
        }
        if (newShowsRepair !== undefined) {
            updatedImage.showsRepair = newShowsRepair;
        }
        if (newComments !== undefined) {
            updatedImage.comments = newComments;
        }

        try {
            const apiResponse = await fileApi.updateFormImageMore({
                formSubmissionId: metaData.submissionId,
                image: updatedImage
            });

            setUploadedImages(prev =>
                prev.map(img =>
                    img.generalFileId === apiResponse.generalFileId ? {...apiResponse} : img
                )
            );
        } catch (e) {
            console.error('Failed to update image:', e);
            setComponentError('Failed to update image. Please try again.');
        } finally {
            setUpdatingImageId(null);
        }
    }, [metaData.submissionId, fileApi, setUploadedImages]);

    const debouncedCommentUpdate = useMemo(
        () => debounce((image: ImageSubmission, text: string) => {
            handleImageUpdate(image, undefined, undefined, text);
        }, 1000),
        [handleImageUpdate] // dependent on your main function
    );

    const imageTypeOptions = useMemo(() => {
        return new Set([
            ...unfulfilledTypes,  // Already FormImageType values, no .map needed
            ...(component.optionalTypes || [])
        ])
    }, [unfulfilledTypes, component.optionalTypes]);

    const removeUploadingFile = (id: string) => {
        setUploadingFiles(prev => prev.filter(f => f.id !== id));
    };

    const validate = useValidationRules({component});


    return (
        <Controller
            name={component.fieldKey}
            control={formMethods.control}
            rules={validate}
            defaultValue={[]}
            render={({field, fieldState}) => {
                return (
                    <>
                        <div className="relative w-full min-h-fit flex">
                            {/* OTHER IMAGES */}
                            <div className="w-4/5 pr-1 flex flex-col">
                                <FormLabel component={component}/>
                                <div className={`flex-1 pr-1 space-y-2 min-h-0 shadow-md border-1 border-gray-200 rounded-md`}>
                                    {sortedImages.length > 0
                                        ? (
                                            <div className="grid grid-cols-3 gap-2">
                                                {sortedImages.map((image: ImageSubmission) => (
                                                    <div
                                                        key={image.generalFileId}
                                                        className={`relative w-full h-fit flex-shrink-0 border-1 bg-gray-600 ${image.imageType === FormImageType.NONE ? "border-red-800" : "border-gray-400"}`}>
                                                        <div className="relative bg-gray-700 aspect-[4/3]">
                                                            <button
                                                                type="button"
                                                                key={`${image.generalFileId}-openModal`}
                                                                className="flex-shrink-0 h-full"
                                                                onClick={() => setSelectedModalImage(image)}
                                                            >
                                                                <ImageDisplay image={image}/>
                                                                {updatingImageId === image.generalFileId &&
                                                                    <Loader className="text-blue-400 animate-spin h-6 w-6 absolute left-1/2 top-1"/>
                                                                }
                                                            </button>
                                                            {image.imageType === FormImageType.NONE &&
                                                                <p className="text-white text-sm bg-red-800/50 text-center absolute bottom-2 w-full">No
                                                                    Label Selected</p>}
                                                            <button type="button"
                                                                    key={`${image.generalFileId}-delete`}
                                                                    onClick={() => handleRemoveUploadedImage(image.generalFileId)}
                                                                    className="absolute top-2 right-2 bg-black/50 hover:bg-red-500 text-white p-1.5 rounded-full transition-colors">
                                                                <X className="h-3 w-3"/></button>
                                                        </div>

                                                        {/* Edit Section */}
                                                        <div className="bg-white border-t py-2 px-3 space-y-1.5">
                                                            <div className="flex items-center gap-2 relative">
                                                                <div className="flex flex-col gap-1">
                                                                    <label
                                                                        className={`text-sm font-medium whitespace-nowrap ${image.imageType === FormImageType.NONE ? "text-red-800" : "text-gray-700"}`}>
                                                                        Image Type:
                                                                    </label>
                                                                    <select
                                                                        value={image.imageType || FormImageType.NONE}
                                                                        onChange={(e) =>
                                                                            handleImageUpdate(image, e.target.value as FormImageType, undefined, undefined)}
                                                                        disabled={updatingImageId === image.generalFileId}
                                                                        className="flex-1 text-xs rounded border border-gray-300"
                                                                    >
                                                                        <option value={FormImageType.NONE}>None</option>
                                                                        {image.imageType !== FormImageType.NONE && <option value={image.imageType}>{getImageTypeDisplay(image.imageType)}</option>}
                                                                        {[...new Set([...imageTypeOptions])]
                                                                            .filter(opt => opt && opt !== FormImageType.NONE)
                                                                            .map(option => (
                                                                                <option key={option} value={option}>
                                                                                    {getImageTypeDisplay(option)}
                                                                                </option>
                                                                            ))}
                                                                    </select>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-1">
                                                                {component.showsRepair &&
                                                                    <div className="flex flex-row gap-1">
                                                                        <label
                                                                            className="block text-xs font-medium text-gray-700"
                                                                            htmlFor={`showsRepair-${image.generalFileId}`}
                                                                        >Is there a repair item shown in this photo?</label>
                                                                        <input type="checkbox"
                                                                               id={`showsRepair-${image.generalFileId}`}
                                                                               name={`showsRepair-${image.generalFileId}`}
                                                                               key={`${image.generalFileId}-repair`}
                                                                               checked={image.showsRepair || false}
                                                                               disabled={updatingImageId === image.generalFileId}
                                                                               onChange={(e) =>
                                                                                   handleImageUpdate(image, undefined, e.target.checked, undefined)}
                                                                        />
                                                                    </div>
                                                                }
                                                            </div>
                                                            <div className="flex gap-1">
                                                                <div className="flex-col items-center">
                                                                    <MessageSquarePlus
                                                                        className="h-8 w-8 text-blue-500"/>
                                                                </div>
                                                                <textarea
                                                                    rows={1}
                                                                    placeholder="Add comment..."
                                                                    onChange={(e) => debouncedCommentUpdate(image, e.target.value)}
                                                                    className="flex-1 text-sm text-black placeholder:text-gray-400 rounded border border-gray-300 px-1"
                                                                    defaultValue={image.comments || ""}
                                                                    key={image.generalFileId}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="rounded-lg bg-gray-50/50 border border-gray-100 flex flex-col justify-center items-center p-8 text-center">
                                                <div className="bg-gray-100 rounded-lg p-3 mb-3">
                                                    <ImageIcon className="h-8 w-8 text-gray-500"/>
                                                </div>
                                                <p className="text-sm font-medium text-gray-700">No images uploaded</p>
                                                <p className="text-xs text-gray-500 mt-1">Select images to begin</p>
                                            </div>
                                        )
                                    }
                                </div>

                                {/* Hidden input to satisfy react-hook-form controller */}
                                <input type="hidden" {...field} />
                            </div>

                            {/* ALL LABELS */}
                            <div className="w-1/5 px-1.5 flex flex-col">
                                <h4 className="ml-2 text-gray-700 text-sm line-clamp-1 max-w-11/12 font-medium group/label">Required
                                    Photos</h4>
                                <div className="min-h-0 shadow-sm border-1 border-gray-200 rounded-md">
                                    <div className="text-xs">
                                        {unfulfilledTypes.map((option, index) => (
                                            <div
                                                key={`${option}.u.${index}`}
                                                className="relative pl-8 pr-5 py-0.5 break-words whitespace-normal opacity-50 text-red-600"
                                            >
                                                {getImageTypeDisplay(option)}
                                            </div>
                                        ))}
                                        {fulfilledTypes.map((option, index) => (
                                            <div
                                                key={`${option}.f.${index}`}
                                                className={`relative pl-8 pr-5 py-0.5 break-words whitespace-normal`}
                                            >
                                                <Check className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4"/>
                                                {getImageTypeDisplay(option)}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                {/* Add More Images Button */}
                                <label className="block my-1">
                                    <div
                                        className="border-2 border-dashed border-gray-300 rounded-md p-3 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all">
                                        <div className="flex items-center justify-center gap-2">
                                            <Upload className="h-4 w-4 text-blue-500"/>
                                            <span
                                                className="text-sm font-medium text-blue-600">Upload Images</span>
                                        </div>
                                    </div>
                                    <input id={component.fieldKey} type="file" accept="image/*" multiple
                                           className="hidden" onChange={handleFileSelection}/>
                                </label>
                                <FormError
                                    fieldState={fieldState}
                                    component={component}
                                />
                                {componentError && (
                                    <div
                                        className="p-2 bg-red-50 border border-red-200 rounded-md flex items-center text-sm">
                                        <AlertCircle className="h-4 w-4 text-red-500 mr-2"/>
                                        <p className="text-red-700">{componentError}</p>
                                    </div>
                                )}
                                {/* Uploading Files List */}
                                {uploadingFiles.length > 0 && (
                                    <div className="space-y-2 p-3">
                                        <p className="ml-2 text-sm font-medium text-gray-600">Uploading...</p>
                                        {uploadingFiles.map(upload => (
                                            <div key={upload.id}
                                                 className="flex items-center justify-between text-sm">
                                                <p className={`truncate pr-4 ${upload.error ? 'text-red-600' : 'text-gray-700'}`}>{upload.file.name}</p>
                                                {upload.error ? (
                                                    <div className='flex items-center gap-2'>
                                                        <span className='text-red-500 text-xs'>{upload.error}</span>
                                                        <button type="button"
                                                                onClick={() => uploadFile(upload.file, upload.id)}
                                                                className="text-blue-500 hover:text-blue-700">
                                                            <RefreshCw className="h-4 w-4"/></button>
                                                        <button type="button"
                                                                onClick={() => removeUploadingFile(upload.id)}
                                                                className="text-gray-400 hover:text-gray-600"><X
                                                            className="h-4 w-4"/></button>
                                                    </div>
                                                ) : (
                                                    <Loader className="animate-spin h-4 w-4 text-blue-500"/>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <FullSizeImageModal selectedModalImage={selectedModalImage}
                                            onClose={() => setSelectedModalImage(null)}/>
                    </>
                );
            }}
        />
    );
}


export const ExpandedImageUpload = React.memo(ExpandedImageUploadRenderer);
